/**
 * Order Tracking Page
 * Displays order status and progress after checkout.
 *
 * Thin orchestrator — composes the sub-components and hooks defined in
 * `./order-tracking/*`. Behavior preserved from the pre-split implementation.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { Button, ThemeSwitcher } from '@/components/ui'
import { useAppSelector } from '@/store/hooks'
import { useOrderTracking } from '@/hooks/useOrderTracking'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import {
  getTrackingStepIndex,
  getTrackingSteps,
  type OrderStatus,
  type OrderType,
} from '@/types/order'
import { useMenuData } from '@/hooks/useMenuData'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'
import {
  NoActiveOrderState,
  OrderCancelCard,
  OrderContactInfoCard,
  OrderEstimatedTimeCard,
  OrderLocationCard,
  OrderStatusCard,
  OrderSupportCard,
  OrderTrackingSkeleton,
  PaymentFailedState,
  useCancelCountdown,
  useCancelOrderFlow,
  useCopyTrackingLink,
  useEstimatedTimeDisplay,
  useOrderHydration,
  useStripeRedirectHandler,
} from './order-tracking'

interface OrderTrackingPageProps {
  navigate: (path: string) => void
  /** Order ID from URL parameter - can be used to fetch order from API */
  orderId?: string
  /** Tracking code from URL parameter - for public shareable links */
  trackingCode?: string
}

// Note: orderId prop is available for future API integration to fetch order details by ID

const CANCELLABLE_STATUSES: OrderStatus[] = ['pending', 'confirmed']

/**
 * Main Order Tracking Page component
 */
export function OrderTrackingPage({
  navigate,
  orderId,
  trackingCode: trackingCodeProp,
}: OrderTrackingPageProps) {
  const { t } = useTranslation()
  const order = useAppSelector((s) => s.order)
  const { restaurant } = useMenuData()
  const { location: selectedLocation, address: locationAddress } = useSelectedLocation()
  const reduxTrackingCode = useAppSelector((s) => s.order.trackingCode)

  // The effective tracking code: prefer the URL param (public link), fall back to Redux state
  const effectiveTrackingCode = trackingCodeProp || reduxTrackingCode

  // Public link = opened via a shared tracking URL (has trackingCode in URL params)
  const isPublicLink = !!trackingCodeProp
  const { data: publicOrderData } = menuWidgetApi.publicOrderTracking.get(
    effectiveTrackingCode ? { trackingCode: effectiveTrackingCode } : undefined,
    { skip: !effectiveTrackingCode }
  )

  // Handle return from Stripe 3D Secure redirect
  const { stripeRedirectError } = useStripeRedirectHandler({ orderId: order.orderId })

  // Hydrate Redux from public API data + safety-net cart clear
  useOrderHydration({
    publicOrderData,
    trackingCodeProp,
    currentOrderStatus: order.orderStatus,
  })

  // Real-time tracking via Firebase RTDB with polling fallback
  const { isRealtime, isPolling } = useOrderTracking({
    orderId: orderId || order.orderId,
    trackingCode: effectiveTrackingCode,
    enabled: !!(orderId || order.orderId || effectiveTrackingCode),
  })

  // Copy-to-clipboard handler for the public tracking link
  const { linkCopied, copy: handleCopyTrackingLink } = useCopyTrackingLink(effectiveTrackingCode)

  // Cancel order flow (confirm dialog + mutation)
  const {
    showCancelConfirm,
    isCancelling,
    cancelError,
    showConfirm,
    hideConfirm,
    cancel: handleCancel,
  } = useCancelOrderFlow({ trackingCode: effectiveTrackingCode })

  // Determine if cancellation is possible — must be in a cancellable status
  // AND within the grace period. The live RTDB status takes priority over
  // cached API data (admin may have advanced the order since the API call).
  const liveStatus = order.orderStatus !== 'idle' ? order.orderStatus : publicOrderData?.status
  const statusAllowsCancel = CANCELLABLE_STATUSES.includes((liveStatus as OrderStatus) || 'idle')
  const canCancel = statusAllowsCancel && (publicOrderData?.can_cancel ?? true)
  const cancelDeadline = publicOrderData?.cancel_deadline || null

  // Countdown timer for grace period — always uses backend cancel_deadline
  const timeLeftMs = useCancelCountdown(cancelDeadline)
  const showCancelButton = canCancel && timeLeftMs !== null && timeLeftMs > 0

  // Merge Redux state with public API data (public API data used when no Redux state)
  const orderNumber = order.orderNumber || publicOrderData?.order_number || null
  const orderType = order.orderType || publicOrderData?.order_type || null
  const orderStatus = order.orderStatus !== 'idle' ? order.orderStatus : (publicOrderData?.status || null)
  const estimatedTime = order.estimatedTime || publicOrderData?.estimated_ready_at || null
  const customerInfo = order.customerInfo
  const deliveryInfo = order.deliveryInfo

  const hasOrder = !!orderNumber

  // Determine tracking steps based on order type
  const effectiveOrderType: OrderType = orderType || 'pickup'
  const effectiveStatus: OrderStatus = orderStatus || 'confirmed'

  const steps = useMemo(
    () => getTrackingSteps(effectiveOrderType),
    [effectiveOrderType]
  )

  const currentStepIndex = useMemo(
    () => getTrackingStepIndex(effectiveStatus, effectiveOrderType),
    [effectiveStatus, effectiveOrderType]
  )

  // Display strings for the ETA card
  const { formattedEta, timeRemaining } = useEstimatedTimeDisplay(estimatedTime)

  // Public links: show skeleton until we have real data — prevents blank/flicker on mobile
  if (isPublicLink && !hasOrder) {
    return <OrderTrackingSkeleton />
  }

  // If no order and status is idle (and no Stripe redirect error), show a "no order" state
  // Don't show this when navigating via a public tracking link (wait for API to load)
  if (!hasOrder && effectiveStatus === 'idle' && !stripeRedirectError && !isPublicLink) {
    return <NoActiveOrderState onBackToMenu={() => navigate('/menu')} />
  }

  // Show Stripe redirect payment failure page
  if (stripeRedirectError) {
    return (
      <PaymentFailedState
        message={stripeRedirectError}
        onBackToCheckout={() => navigate('/checkout')}
      />
    )
  }

  // Check if order is in error or cancelled state
  const isErrorState = effectiveStatus === 'error' || effectiveStatus === 'cancelled'

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/menu')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('common:backToMenu')}
          </Button>
          <ThemeSwitcher />
        </div>

        <OrderStatusCard
          orderNumber={orderNumber}
          orderType={effectiveOrderType}
          status={effectiveStatus}
          isErrorState={isErrorState}
          isRealtime={isRealtime}
          isPolling={isPolling}
          currentStepIndex={currentStepIndex}
          steps={steps}
          trackingCode={effectiveTrackingCode}
          linkCopied={linkCopied}
          onCopyLink={handleCopyTrackingLink}
        />

        {/* Cancellation Section */}
        {showCancelButton && !isErrorState && (
          <OrderCancelCard
            showCancelConfirm={showCancelConfirm}
            isCancelling={isCancelling}
            cancelError={cancelError}
            timeLeftMs={timeLeftMs!}
            onShowConfirm={showConfirm}
            onCancel={handleCancel}
            onKeep={hideConfirm}
          />
        )}

        {/* Estimated Time Card — hide when order is completed */}
        {(formattedEta || timeRemaining) && !isErrorState && effectiveStatus !== 'completed' && (
          <OrderEstimatedTimeCard
            orderType={effectiveOrderType}
            formattedEta={formattedEta}
            timeRemaining={timeRemaining}
          />
        )}

        <OrderLocationCard
          orderType={effectiveOrderType}
          deliveryInfo={deliveryInfo}
          restaurantTitle={restaurant?.title}
          locationAddress={locationAddress}
          selectedLocationPhone={selectedLocation?.phone}
        />

        {customerInfo && <OrderContactInfoCard customerInfo={customerInfo} />}

        <OrderSupportCard
          phone={selectedLocation?.phone}
          email={restaurant?.email}
        />

        {/* Back to Menu Button */}
        <div className="text-center">
          <Button
            variant="black"
            size="lg"
            className="w-full sm:w-auto rounded-full px-8"
            onClick={() => navigate('/menu')}
          >
            {t('common:backToMenu')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export { OrderTrackingSkeleton }
